/**
 * Barre latérale : Métiers › Modules › Sous-modules (modules activés pour la filiale et lisibles uniquement), Pilotage,
 * Référentiels, et Administration pour les administrateurs.
 */
import React from 'react';
import type { MetierId, NoeudCatalogue } from '@agora/shared';
import { Sidebar, type SidebarLeaf, type SidebarMetier, type SidebarProps } from '../../design';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import type { Noeud } from '@agora/shared';
import { useActionsEspace } from '../pages/espace';

/**
 * Écrans d'administration : modules par filiale, droits et groupes (lecture seule) pour les administrateurs ;
 * signatures e-mail PRODEVAL pour le droit « administrer » sur la messagerie (marketing).
 */
export const ROUTE_ADMIN_MODULES = '/administration/modules';
const ID_ADMIN_MODULES = 'admin::modules';
export const ROUTE_ADMIN_DROITS = '/administration/droits';
const ID_ADMIN_DROITS = 'admin::droits';
export const ROUTE_ADMIN_SIGNATURES = '/outils/messagerie/signatures';
const ID_ADMIN_SIGNATURES = 'admin::signatures';

export function LogoProdeval({ hauteur = 16 }: { hauteur?: number }) {
  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- nom propre (marque), identique dans toutes les langues */}
      <img className="ag-logo-light" src="/design/prodeval-logo.svg" alt="PRODEVAL" style={{ height: hauteur }} />
      {/* eslint-disable-next-line no-restricted-syntax -- nom propre (marque), identique dans toutes les langues */}
      <img className="ag-logo-dark" src="/design/prodeval-logo-reverse.svg" alt="PRODEVAL" style={{ height: hauteur }} />
    </>
  );
}

export function BarreLaterale({ actif, chemin, replie, onBasculer }: { actif?: Noeud; chemin: string; replie: boolean; onBasculer: () => void }) {
  const { t, lib } = useT();
  const { catalogue, moi, noeuds } = useSocle();
  const { ouvrir } = useActionsEspace();
  // Ouverture des menus : pilotée ici pour déplier automatiquement les parents de la page active.
  const [ouverts, setOuverts] = React.useState<Record<string, boolean>>({});
  React.useEffect(() => {
    if (!actif) return;
    const aOuvrir = [...actif.parents, ...(actif.metier && actif.type === 'Metier' ? [actif.id] : [])];
    if (aOuvrir.length) setOuverts((o) => ({ ...o, ...Object.fromEntries(aOuvrir.map((id) => [id, true])) }));
  }, [actif]);

  const feuille = (n: NoeudCatalogue): SidebarLeaf => ({
    id: n.id, label: lib(n.id), href: n.route, open: !!ouverts[n.id],
    children: n.enfants.length ? n.enfants.map(feuille) : undefined,
  });
  const sections: SidebarProps['sections'] = [];
  if (catalogue) {
    sections.push({
      title: t('nav.metiers'),
      items: catalogue.metiers.map((m): SidebarMetier => ({
        id: m.id, label: lib(m.id), metier: m.id as MetierId, open: !!ouverts[m.id],
        children: [{ id: `${m.id}::accueil`, label: t('nav.accueilMetier'), href: m.route }, ...m.enfants.map(feuille)],
      })),
    });
    if (catalogue.pilotage.length) sections.push({ title: t('nav.pilotage'), items: catalogue.pilotage.map((p) => ({ id: p.id, label: lib(p.id), icon: p.icone, href: p.route })) });
    if (catalogue.dimensions.length) sections.push({ title: t('nav.referentiels'), items: catalogue.dimensions.map((d) => ({ id: d.id, label: lib(d.id), icon: d.icone, href: d.route })) });
  }
  const signatures = !!noeuds.get('outils.messagerie')?.droits.administrer;
  if (moi.admin || signatures) {
    sections.push({
      title: t('nav.administration'),
      items: [
        ...(moi.admin ? [
          { id: ID_ADMIN_MODULES, label: t('admin.modules.titre'), icon: 'settings' as const, href: ROUTE_ADMIN_MODULES },
          { id: ID_ADMIN_DROITS, label: t('admin.droits.titre'), icon: 'key' as const, href: ROUTE_ADMIN_DROITS },
        ] : []),
        ...(signatures ? [{ id: ID_ADMIN_SIGNATURES, label: t('outils.messagerie.signatures.acces'), icon: 'mail' as const, href: ROUTE_ADMIN_SIGNATURES }] : []),
      ],
    });
  }
  const idActif = chemin.startsWith(ROUTE_ADMIN_MODULES) ? ID_ADMIN_MODULES
    : chemin.startsWith(ROUTE_ADMIN_DROITS) ? ID_ADMIN_DROITS
    : chemin.startsWith(ROUTE_ADMIN_SIGNATURES) ? ID_ADMIN_SIGNATURES
    : actif ? (actif.type === 'Metier' ? `${actif.id}::accueil` : actif.id) : undefined;

  return (
    <Sidebar
      ariaLabel={t('nav.navigation')}
      brand="AGORA"
      collapsed={replie}
      onToggle={onBasculer}
      collapseLabel={t('nav.replier')}
      expandLabel={t('nav.deplier')}
      activeId={idActif}
      sections={sections}
      onToggleItem={(id) => setOuverts((o) => ({ ...o, [id]: !o[id] }))}
      onMetier={(id) => ouvrir(`/${id}`)}
      footer={<LogoProdeval />}
    />
  );
}
